import { ApiService } from '@supply-mover-2-0/core';
import type { ReactNode } from 'react';
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslation } from '../../../providers/TranslationProvider';
import { Toast } from '../../../services/ToastService';

export interface BulkActionStatusModel {
	id: number;
	listKey: string;
	actionType: string;
	targetListId: number | null;
	status: 'pending' | 'processing' | 'completed' | 'failed';
	totalCount: number;
	completedCount: number;
	failedCount: number;
	startedAt: Date | null;
	completedAt: Date | null;
	errorMessage: string | null;
	createdDate: Date;
}

const POLL_INTERVAL_MS = 2000; // tight loop while we know an action is pending
const IDLE_POLL_INTERVAL_MS = 15_000; // slow background poll so cross-page completion toasts still fire

// Module-scoped state that survives provider remounts. The provider
// unmounts and remounts on every route change (the auth-state branch in
// `apps/crm-web/src/App.tsx` flickers between trees mid-navigation, and
// even if it didn't, React StrictMode in dev double-invokes mount), so
// we cannot rely on useRef alone — refs are tied to the React instance
// and reset on remount. Keeping the seen-action ledger at module scope
// is what makes the cross-page toast actually fire: the new provider on
// the destination page reads the same ledger and recognises that an
// action it's seeing for the first time is "new since our last poll."
let sharedSeenStatuses: Map<number, BulkActionStatusModel['status']> =
	new Map();
// "Baseline" id: the cutoff above which actions are considered "new
// during this session" and eligible to toast. Seeded in `triggerPoll`
// to `actionId - 1` of the first action the user kicks off, so the
// just-submitted action lands above the cutoff and toasts when it
// terminates. There is also a defensive fallback in `fetchStatus` for
// the unusual case where polling is armed without `triggerPoll` having
// seeded a baseline. Pre-existing already-terminal actions returned on
// initial fetch fall at or below the cutoff and are ignored, so we
// don't retroactively toast for stuff from a prior session.
let sharedBaselineId: number | null = null;
// Whether the polling loop should currently be running. Toggled on by
// `triggerPoll` (when the user kicks off a bulk action) and back off
// inside `fetchStatus` once a poll returns with nothing pending. While
// true, it survives provider remounts so a navigation mid-action keeps
// polling on the destination page (the freshly-mounted provider reads
// the still-true flag via its useState initializer). Users who never
// bulk-edit, and users whose actions have all already completed, incur
// zero status fetches.
let sharedShouldPoll = false;

function getActionLabel(actionType: string, t: (s: string) => string): string {
	switch (actionType) {
		case 'add-to-list':
			return t('Adding to list');
		case 'remove-from-list':
			return t('Removing from list');
		case 'assign-sales-rep':
			return t('Assigning sales rep');
		case 'unassign-sales-rep':
			return t('Unassigning sales rep');
		case 'assign-alternate-rep':
			return t('Assigning alternate rep');
		case 'unassign-alternate-rep':
			return t('Unassigning alternate rep');
		case 'assign-to-group':
			return t('Assigning to group');
		case 'remove-from-group':
			return t('Removing from group');
		case 'change-lifecycle-stage':
			return t('Changing lifecycle stage');
		case 'distribute-leads':
			return t('Distributing leads');
		case 'delete-clients':
			return t('Deleting clients');
		case 'undelete-clients':
			return t('Recovering clients');
		case 'update-custom-field':
			return t('Updating custom field');
		case 'update-field':
			return t('Updating field');
		default:
			return t('Bulk action');
	}
}

export interface UseBulkActionPollingOptions {
	/**
	 * Called after the polling loop sees one or more actions transition
	 * from pending/processing → completed/failed. The provider tracks
	 * all bulk actions for its `listKey`; consumers usually use this to
	 * refetch the list view in response.
	 */
	onActionComplete?: () => void;
}

export interface UseBulkActionPollingResult {
	/** Call this after submitting a bulk action to immediately start polling.
	 *  Pass the bulkActionId so fast-completing actions are tracked even if
	 *  they finish before the first poll. */
	triggerPoll: (actionId?: number) => void;
}

interface BulkActionPollingContextValue {
	triggerPoll: (actionId?: number) => void;
	subscribe: (callback: () => void) => () => void;
}

const BulkActionPollingContext =
	createContext<BulkActionPollingContextValue | null>(null);

export interface BulkActionPollingProviderProps {
	/** Which list's bulk actions to poll. Currently only 'client' is wired
	 * server-side; one provider per list-type would be needed if the
	 * status endpoint expands to other lists. */
	listKey: string;
	children: ReactNode;
}

/**
 * Mounts the bulk-action polling state at a single point in the tree
 * (typically the app shell) so completion toasts fire regardless of
 * which page the user is on when the queued job finishes. Before this
 * was lifted, polling lived inside the list view itself — so navigating
 * away mid-action discarded the witnessed-pending set and the user
 * never saw the completion toast.
 */
export function BulkActionPollingProvider({
	listKey,
	children,
}: BulkActionPollingProviderProps): ReactNode {
	const { t } = useTranslation();

	// Three states:
	//   - Dormant (shouldPoll === false): no polling at all. Users who
	//     never bulk-edit pay zero status-endpoint cost. Flips to true
	//     the first time anyone calls `triggerPoll` in this session, and
	//     stays true (mirrored to module-scoped `sharedShouldPoll` so it
	//     survives provider remounts on navigation).
	//   - Idle (shouldPoll && !hasActiveActions): poll every
	//     IDLE_POLL_INTERVAL_MS. Needed for the cross-page case — user
	//     submits on page A, navigates to B mid-action; the new provider
	//     on B must keep polling to catch the eventual completion.
	//   - Active (shouldPoll && hasActiveActions): poll every
	//     POLL_INTERVAL_MS for snappy feedback while something is known
	//     to be pending.
	const [shouldPoll, setShouldPoll] = useState(sharedShouldPoll);
	const [hasActiveActions, setHasActiveActions] = useState(false);
	const pollRef = useRef<NodeJS.Timeout | null>(null);
	// Tracks the last status we observed for each action id. Lets us
	// detect transitions (pending/processing → completed/failed) AND
	// first-sightings of recently-terminal actions (the cross-page case:
	// user submits on page A, navigates to page B, our next poll on B
	// sees the action already completed without us having witnessed the
	// pending state).
	// All long-lived bookkeeping lives in the module-scoped `shared*`
	// vars (see top of file). Provider remounts on navigation, so per-
	// instance refs would lose the witness state. The only thing kept
	// per-instance is the subscriber set for the onActionComplete
	// callbacks consumers register via useBulkActionPolling.
	const subscribersRef = useRef<Set<() => void>>(new Set());

	const fetchStatus = useCallback(async (): Promise<void> => {
		try {
			const result = await ApiService.apiGet<{
				success: boolean;
				data: Array<BulkActionStatusModel>;
				error?: string;
			}>('/clients/bulk-actions/status', { listKey });

			if (result?.success && result.data != null) {
				const data = result.data;

				// Fallback baseline init for the unusual case where polling is
				// armed without `triggerPoll` having seeded a baseline (e.g.
				// future call site that flips `sharedShouldPoll` directly).
				// The normal path is for `triggerPoll(actionId)` to set
				// baseline = actionId - 1 BEFORE this fetch fires — that's
				// what makes the just-submitted action recognised as new.
				// Freezing to max-id here would (and did) suppress the toast
				// for the action we just kicked off, so this branch is now
				// pure defense-in-depth.
				if (sharedBaselineId == null) {
					const maxInResponse = data.reduce(
						(max, a) => (a.id > max ? a.id : max),
						0,
					);
					sharedBaselineId = maxInResponse;
				}
				const baseline = sharedBaselineId;

				const newlyCompleted: Array<BulkActionStatusModel> = [];

				data.forEach((action) => {
					const prev = sharedSeenStatuses.get(action.id);
					sharedSeenStatuses.set(action.id, action.status);

					// Anything ≤ baseline existed when the page loaded — don't
					// retroactively toast for those.
					if (action.id <= baseline) {
						return;
					}
					// Already-shown terminal action — skip.
					if (prev === 'completed' || prev === 'failed') {
						return;
					}
					const isTerminalNow =
						action.status === 'completed' || action.status === 'failed';
					if (!isTerminalNow) {
						return;
					}
					// Either we watched it run (prev was pending/processing)
					// or this is a first sighting in a terminal state (cross-
					// page case). In both branches, the id > baseline guard
					// already established it's a new-during-session action,
					// so just toast.
					newlyCompleted.push(action);
				});

				let didComplete = false;
				newlyCompleted.forEach((action: BulkActionStatusModel) => {
					const label = getActionLabel(action.actionType, t);
					if (action.status === 'completed') {
						const failed = action.failedCount;
						if (action.actionType === 'distribute-leads') {
							if (action.totalCount === 0) {
								Toast.warning(`${label}: ${t('No leads found in selection')}`);
							} else if (failed > 0) {
								Toast.success(
									`${label}: ${t('Distributed')} ${String(action.completedCount)} ${t('of')} ${String(action.totalCount)} ${t('leads')}, ${String(failed)} ${t('failed')}`,
								);
							} else {
								Toast.success(
									`${label}: ${t('Distributed')} ${String(action.completedCount)} ${t('of')} ${String(action.totalCount)} ${t('leads')}`,
								);
							}
						} else if (failed > 0) {
							Toast.success(
								`${label}: ${String(action.completedCount - failed)} ${t('updated')}, ${String(failed)} ${t('failed')}`,
							);
						} else {
							Toast.success(
								`${label}: ${String(action.completedCount)} ${t('clients updated')}`,
							);
						}
						didComplete = true;
					} else if (action.status === 'failed') {
						Toast.error(action.errorMessage ?? `${label} ${t('failed')}`);
						didComplete = true;
					}
				});

				if (didComplete) {
					subscribersRef.current.forEach((cb) => {
						try {
							cb();
						} catch (error) {
							console.error('Bulk action subscriber threw:', error);
						}
					});
				}

				const active = data.some(
					(a) => a.status === 'pending' || a.status === 'processing',
				);
				setHasActiveActions(active);

				// Wind polling all the way down once nothing in the response is
				// pending. By this point we've already fired toasts for anything
				// that just transitioned in this fetch, so there's no work left
				// to watch for. Future bulk actions will re-arm polling via
				// `triggerPoll`. Note: `sharedSeenStatuses` / `sharedBaselineId`
				// intentionally stay populated so we never re-toast something
				// we already showed.
				if (!active) {
					sharedShouldPoll = false;
					setShouldPoll(false);
				}
			}
		} catch (error) {
			console.error('Error fetching bulk action status:', error);
		}
	}, [listKey, t]);

	useEffect(() => {
		// Stay completely dormant until something has happened. The first
		// `triggerPoll` of the session flips `sharedShouldPoll` → true and
		// updates local `shouldPoll`, which re-runs this effect and starts
		// the loop. Subsequent provider remounts (route changes) read the
		// already-true module flag from the useState initializer.
		if (!shouldPoll) {
			return undefined;
		}

		void fetchStatus();

		const interval = hasActiveActions
			? POLL_INTERVAL_MS
			: IDLE_POLL_INTERVAL_MS;
		pollRef.current = setInterval(() => {
			void fetchStatus();
		}, interval);

		return () => {
			if (pollRef.current != null) {
				clearInterval(pollRef.current);
				pollRef.current = null;
			}
		};
	}, [fetchStatus, hasActiveActions, shouldPoll]);

	const triggerPoll = useCallback(
		// Seed the baseline to actionId - 1 if this is the first arming
		// of polling in the session. Without this seed, the first fetch
		// would set the baseline to the max id in the response — which
		// includes the action we just submitted — and the new action
		// would be filtered out as "pre-existing" and never toast.
		// Subsequent submits don't need to update the baseline: their
		// action ids are already above the seeded value.
		(actionId?: number) => {
			if (sharedBaselineId == null && actionId != null) {
				sharedBaselineId = actionId - 1;
			}
			sharedShouldPoll = true;
			setShouldPoll(true);
			setHasActiveActions(true);
			void fetchStatus();
		},
		[fetchStatus],
	);

	const subscribe = useCallback((callback: () => void) => {
		subscribersRef.current.add(callback);
		return () => {
			subscribersRef.current.delete(callback);
		};
	}, []);

	const value = useMemo<BulkActionPollingContextValue>(
		() => ({ triggerPoll, subscribe }),
		[triggerPoll, subscribe],
	);

	return (
		<BulkActionPollingContext.Provider value={value}>
			{children}
		</BulkActionPollingContext.Provider>
	);
}

/**
 * Hook that reads the global bulk-action polling context. Registers
 * `onActionComplete` (typically a list refetch) for the duration of
 * the component's lifetime, and returns `triggerPoll` for callers to
 * invoke after submitting a new bulk action.
 *
 * Must be used inside a `BulkActionPollingProvider` — the provider
 * owns the polling state so it persists across route changes.
 */
export function useBulkActionPolling(
	options: UseBulkActionPollingOptions = {},
): UseBulkActionPollingResult {
	const ctx = useContext(BulkActionPollingContext);
	if (ctx == null) {
		throw new Error(
			'useBulkActionPolling must be used within a BulkActionPollingProvider. Mount one at the app shell.',
		);
	}

	const { onActionComplete } = options;

	useEffect(() => {
		if (onActionComplete == null) {
			return undefined;
		}
		return ctx.subscribe(onActionComplete);
	}, [ctx, onActionComplete]);

	return { triggerPoll: ctx.triggerPoll };
}
